import {
  Banknote,
  BriefcaseMedical,
  Calendar,
  ChevronDown,
  Clock3,
  Fingerprint,
  Loader2,
  Pencil,
  Save,
  Search,
  User,
  UserPlus,
  X,
} from 'lucide-react'

import { PROCEDURE_OPTIONS } from '../constants/app'
import { cleanCPF, formatCPF } from '../utils/formatters'

export default function PatientModal({
  error,
  form,
  isSubmitting,
  isSearchingPatients,
  mode,
  onChange,
  onClose,
  onSelectPatient,
  onSubmit,
  patientSuggestions,
}) {
  const isEditing = mode === 'edit'
  const HeaderIcon = isEditing ? Pencil : UserPlus

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#1A1A1A]/35 p-4 backdrop-blur-[5px] max-sm:items-end max-sm:p-2"
      role="presentation"
      onMouseDown={onClose}
    >
      <section
        className="w-full max-w-[680px] overflow-hidden rounded-xl bg-white shadow-[0_22px_55px_rgba(17,24,39,0.22)] max-sm:flex max-sm:max-h-[calc(100dvh-1rem)] max-sm:flex-col max-sm:rounded-b-none max-sm:rounded-t-xl sm:max-w-[580px]"
        role="dialog"
        aria-modal="true"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex min-h-[64px] items-center justify-between gap-3 border-b border-[#E5DED3] px-4 max-sm:min-h-[60px] max-sm:shrink-0 sm:min-h-[76px] sm:px-8">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#F3E8CE] text-[#8A6819] max-sm:shrink-0 sm:h-10 sm:w-10">
              <HeaderIcon size={18} strokeWidth={2.4} aria-hidden="true" />
            </div>
            <h2 className="min-w-0 text-base font-semibold leading-tight tracking-normal text-[#1A1A1A] sm:text-lg sm:text-2xl">
              {isEditing ? 'Editar Paciente' : 'Cadastrar Novo Paciente'}
            </h2>
          </div>

          <button
            className="flex h-9 w-9 items-center justify-center rounded-lg text-[#1A1A1A] transition hover:bg-[#FBF8F4] sm:h-10 sm:w-10"
            type="button"
            onClick={onClose}
            aria-label="Fechar"
          >
            <X size={16} strokeWidth={2.1} className="sm:block hidden" />
            <X size={18} strokeWidth={2.1} className="block sm:hidden" />
          </button>
        </div>

        <form className="max-sm:flex max-sm:min-h-0 max-sm:flex-1 max-sm:flex-col" onSubmit={onSubmit}>
          <div className="grid gap-4 px-4 py-5 max-sm:min-h-0 max-sm:flex-1 max-sm:overflow-y-auto max-sm:overscroll-contain max-sm:py-4 sm:gap-5 sm:px-8 sm:py-6">
            {error ? (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700">
                {error}
              </div>
            ) : null}

            <ModalField icon={User} label="Nome completo">
              <div className="relative">
                <input
                  className="modal-input"
                  autoComplete="off"
                  aria-autocomplete="list"
                  minLength="2"
                  placeholder="Nome completo"
                  required
                  value={form.name}
                  onChange={(event) => onChange('name', event.target.value)}
                />
                {isSearchingPatients ? (
                  <Loader2
                    className="absolute right-4 top-1/2 -translate-y-1/2 animate-spin text-[#8A6819]"
                    size={17}
                    aria-label="Buscando paciente"
                  />
                ) : null}
                {patientSuggestions.length > 0 ? (
                  <div className="absolute left-0 right-0 top-[calc(100%+0.35rem)] z-20 overflow-hidden rounded-lg border border-[#D6CDBF] bg-white shadow-[0_12px_24px_rgba(26,26,26,0.14)]">
                    <div className="border-b border-[#EEE8DF] px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#8D857C]">
                      Pacientes encontrados
                    </div>
                    {patientSuggestions.map((patient) => (
                      <button
                        className="flex w-full items-start gap-3 px-3 py-2.5 text-left transition hover:bg-[#FBF5E8]"
                        key={`${patient.id}-${patient.name}`}
                        type="button"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => onSelectPatient(patient)}
                      >
                        <Search className="mt-0.5 shrink-0 text-[#8A6819]" size={15} aria-hidden="true" />
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-bold text-[#1A1A1A]">{patient.name}</span>
                          <span className="block truncate text-xs text-[#7A7269]">
                            {patient.cpf ? 'CPF cadastrado' : 'Sem CPF cadastrado'}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </ModalField>

            <ModalField icon={BriefcaseMedical} label="Procedimento">
              <select
                className="modal-input appearance-none"
                required
                value={form.procedure}
                onChange={(event) => onChange('procedure', event.target.value)}
              >
                <option value="">Selecione o procedimento</option>
                {form.procedure && !PROCEDURE_OPTIONS.includes(form.procedure) ? (
                  <option value={form.procedure}>{form.procedure}</option>
                ) : null}
                {PROCEDURE_OPTIONS.map((procedure) => (
                  <option value={procedure} key={procedure}>
                    {procedure}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="pointer-events-none absolute right-4 top-[38px] text-[#7A7269]"
                size={18}
                strokeWidth={2.3}
                aria-hidden="true"
              />
            </ModalField>

            <div className="grid gap-5 md:grid-cols-2">
              <ModalField icon={Banknote} label="Valor (R$)">
                <input
                  className="modal-input"
                  min="0"
                  placeholder="0,00"
                  required
                  step="0.01"
                  type="number"
                  value={form.value}
                  onChange={(event) => onChange('value', event.target.value)}
                />
              </ModalField>

              <ModalField icon={Clock3} label="Tempo estimado (minutos)">
                <input
                  className="modal-input"
                  min="1"
                  placeholder="Ex: 45"
                  required
                  type="number"
                  value={form.duration_minutes}
                  onChange={(event) => onChange('duration_minutes', event.target.value)}
                />
              </ModalField>
            </div>

            <ModalField icon={Calendar} label="Data">
              <input
                className="modal-input w-full"
                type="date"
                value={form.date}
                onChange={(event) => onChange('date', event.target.value)}
              />
            </ModalField>

            <div className="grid gap-2">
              <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[#1A1A1A]">
                Tipo de atendimento
              </p>
              <div className="grid h-11 grid-cols-2 rounded-lg border border-[#D6CDBF] bg-white p-1">
                <PaymentOption
                  isActive={form.payment_type === 'particular'}
                  label="Particular"
                  onClick={() => onChange('payment_type', 'particular')}
                />
                <PaymentOption
                  isActive={form.payment_type === 'convenio'}
                  label="Convênio"
                  onClick={() => onChange('payment_type', 'convenio')}
                />
              </div>
            </div>

            {form.payment_type === 'particular' && (
              <ModalField icon={Fingerprint} label="CPF">
                <input
                  className="modal-input"
                  maxLength="14"
                  placeholder="000.000.000-00"
                  value={formatCPF(form.cpf || '')}
                  onChange={(event) => onChange('cpf', cleanCPF(event.target.value))}
                />
              </ModalField>
            )}
          </div>

          <div className="flex min-h-[76px] flex-col-reverse items-stretch justify-end gap-3 border-t border-[#E5DED3] bg-[#FBFAF8] px-4 py-4 max-sm:shrink-0 max-sm:pb-[max(1rem,env(safe-area-inset-bottom))] sm:flex-row sm:items-center sm:px-8 sm:py-0">
            <button
              className="h-10 px-4 text-sm font-semibold tracking-[0.04em] text-[#1A1A1A] max-sm:h-11"
              type="button"
              onClick={onClose}
            >
              Cancelar
            </button>
            <button
              className="flex h-10 items-center justify-center gap-2 rounded-full bg-[#1A1A1A] px-5 text-sm font-bold tracking-[0.02em] text-white shadow-sm transition hover:bg-[#2C2A28] disabled:cursor-not-allowed disabled:opacity-70 max-sm:h-11"
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              {isEditing ? 'Salvar Alterações' : 'Salvar Paciente'}
            </button>
          </div>
        </form>
      </section>
    </div>
  )
}

function ModalField({ children, icon: Icon, label }) {
  return (
    <label className="modal-field relative grid gap-2">
      <span className="text-sm font-semibold uppercase tracking-[0.14em] text-[#1A1A1A]">
        {label}
      </span>
      <Icon
        className="pointer-events-none absolute left-4 top-[38px] text-[#7A7269]"
        size={18}
        strokeWidth={2.2}
        aria-hidden="true"
      />
      {children}
    </label>
  )
}

function PaymentOption({ isActive, label, onClick }) {
  return (
    <button
      className={`rounded-md text-sm font-semibold tracking-[0.08em] transition ${
        isActive
          ? 'bg-[#1A1A1A] text-white shadow-[0_6px_16px_rgba(26,26,26,0.16)]'
          : 'text-[#5A5147] hover:bg-[#F8E1E7] hover:text-[#1A1A1A]'
      }`}
      type="button"
      onClick={onClick}
    >
      {label}
    </button>
  )
}
